import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, Plus, CalendarX } from "lucide-react";
import { toast } from "sonner";
import { Section, Empty, Pill, fmtDate, fmtDateTime } from "@/components/services/Shared";

// Dialog: lots d'un médicament + réception directe d'un lot
export function BatchesDialog({ med, suppliers, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ lot_number: "", expiry_date: "", quantity: 1, supplier_id: "", unit_cost_cdf: "" });
  const load = () => med && axios.get(`${API}/services/pharmacie/medications/${med.id}/batches`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [med?.id]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/services/pharmacie/medications/${med.id}/batches`, { ...form, quantity: Number(form.quantity), supplier_id: form.supplier_id || null, unit_cost_cdf: form.unit_cost_cdf === "" ? null : Number(form.unit_cost_cdf), expiry_date: form.expiry_date || null });
      toast.success("Lot réceptionné"); setForm({ lot_number: "", expiry_date: "", quantity: 1, supplier_id: "", unit_cost_cdf: "" }); load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={!!med} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Lots — {med?.name} {med?.dosage}</DialogTitle></DialogHeader>
        {data && (
          <div className="space-y-5" data-testid="batches-dialog">
            <div className="text-sm text-[#5C6B61]">Stock total : <strong className="text-[#1E4631]">{data.medication.stock} {data.medication.unit}</strong> · Les sorties suivent la règle FEFO (premier périmé, premier sorti).</div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-[#5C6B61]"><th className="py-1.5">Lot</th><th>Péremption</th><th>Restant / initial</th><th>Fournisseur</th><th>Coût unit.</th><th>Reçu le</th></tr></thead>
              <tbody className="divide-y divide-[#E5EBE7]">
                {data.batches.length === 0 && <tr><td colSpan={6}><Empty>Aucun lot.</Empty></td></tr>}
                {data.batches.map((b) => (
                  <tr key={b.id} className={b.quantity <= 0 ? "opacity-50" : ""} data-testid={`batch-${b.id}`}>
                    <td className="py-1.5 font-medium">{b.lot_number}</td>
                    <td>{b.is_expired ? <Pill color="#C62828">Périmé {fmtDate(b.expiry_date)}</Pill> : fmtDate(b.expiry_date)}</td>
                    <td>{b.quantity} / {b.initial_quantity}</td><td className="text-[#5C6B61]">{b.supplier_name || "—"}</td>
                    <td className="text-[#5C6B61]">{b.unit_cost_cdf ? `${b.unit_cost_cdf.toLocaleString("fr-FR")} CDF` : "—"}</td>
                    <td className="text-xs text-[#5C6B61]">{fmtDate(b.received_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <form onSubmit={submit} className="border border-[#E5EBE7] rounded-xl p-4 space-y-3 bg-[#FDFBF7]" data-testid="batch-form">
              <div className="font-semibold text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Réceptionner un lot</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label>N° de lot</Label><Input required value={form.lot_number} onChange={(e) => setForm({ ...form, lot_number: e.target.value })} data-testid="batch-lot" /></div>
                <div><Label>Péremption</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="batch-expiry" /></div>
                <div><Label>Quantité</Label><Input type="number" min="1" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="batch-qty" /></div>
                <div className="col-span-2"><Label>Fournisseur</Label>
                  <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}><SelectTrigger data-testid="batch-supplier"><SelectValue placeholder="— Aucun —" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Coût unitaire (CDF)</Label><Input type="number" min="0" value={form.unit_cost_cdf} onChange={(e) => setForm({ ...form, unit_cost_cdf: e.target.value })} data-testid="batch-cost" /></div>
              </div>
              <Button type="submit" size="sm" className="bg-[#1E4631] text-white" data-testid="batch-save">Ajouter le lot</Button>
            </form>
            <div>
              <div className="font-semibold text-sm mb-2">Mouvements récents</div>
              <div className="max-h-48 overflow-y-auto divide-y divide-[#E5EBE7] text-xs">
                {data.movements.map((mv, i) => <div key={i} className="py-1.5 flex justify-between gap-2"><span className="text-[#5C6B61]">{fmtDateTime(mv.at)} · {mv.lot_number || "—"} · {mv.reason}</span><span className={`font-semibold ${mv.delta < 0 ? "text-[#C62828]" : "text-[#2E7D32]"}`}>{mv.delta > 0 ? "+" : ""}{mv.delta}</span></div>)}
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Onglet : lots qui expirent
export function ExpiringTab() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/services/pharmacie/expiring`, { params: { days } }).then((r) => setData(r.data)).catch(() => {}); }, [days]);
  return (
    <Section title="Lots périmés ou proches de la péremption" icon={CalendarX} testid="expiring-tab"
      action={<Select value={String(days)} onValueChange={(v) => setDays(Number(v))}><SelectTrigger className="w-40" data-testid="expiring-days"><SelectValue /></SelectTrigger>
        <SelectContent>{[30, 60, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>Dans {d} jours</SelectItem>)}</SelectContent></Select>}>
      {!data ? <Empty>Chargement…</Empty> : data.items.length === 0 ? <Empty>Aucun lot concerné. Stock sain.</Empty> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-[#5C6B61]"><th className="py-2">Médicament</th><th>Lot</th><th>Péremption</th><th>Quantité</th><th>Fournisseur</th><th>État</th></tr></thead>
          <tbody className="divide-y divide-[#E5EBE7]">
            {data.items.map((b) => <tr key={b.id} data-testid={`expiring-${b.id}`}><td className="py-2 font-medium">{b.medication_name} <span className="text-xs text-[#5C6B61] font-normal">{b.dosage}</span></td><td>{b.lot_number}</td><td>{fmtDate(b.expiry_date)}</td><td>{b.quantity} {b.unit}</td><td className="text-[#5C6B61]">{b.supplier_name || "—"}</td><td><Pill color={b.is_expired ? "#C62828" : "#E3A72F"}>{b.is_expired ? "Périmé — à retirer" : "Expire bientôt"}</Pill></td></tr>)}
          </tbody>
        </table>
      )}
    </Section>
  );
}

export const BatchesIcon = Layers;
